import { Device } from "mediasoup-client";
import { io, Socket } from "socket.io-client";

/**
 * Simple event emitter for handling MediaSoup service events
 */
class SimpleEventEmitter {
  private events: Record<string, Function[]> = {};
  on(event: string, cb: Function) { (this.events[event] ||= []).push(cb); }
  off(event: string, cb: Function) { this.events[event] = (this.events[event] || []).filter(f => f !== cb); }
  emit(event: string, payload?: any) { (this.events[event] || []).forEach(f => f(payload)); }
}

export type HandRaiseRequest = {
  requestId: string;
  streamId: string;
  userId?: string;
  raised: boolean;
  timestamp?: number | string;
};

export type GenericRequestAckDto = {
  requestId: string;
  streamId: string;
  type: 'mute' | 'unmute' | 'host' | 'co-host' | 'hand-raise' | 'hand-lower';
  received: boolean;
  processed?: boolean;
  message?: string;
  payload?: any;
};

// Optional ack options for admin methods (keeps function NAMES the same)
type AckOptions = {
  requestId?: string;
  processed?: boolean;
  message?: string;
  payload?: any;
};

/**
 * MediaSoup Service for handling WebRTC streaming functionality
 * Manages device connections, transports, consumers, and media streams
 */
class MediasoupService {
  private static instance: MediasoupService | null = null;
  private device: Device | null = null;
  private serverUrl: string;
  private socket: Socket | null = null;
  private consumers: Map<string, any> = new Map();
  private consumerTransport: any = null;
  private producerTransport: any = null;
  private roomId: string | null = null;
  private activeConsumers: Map<string, any> = new Map();
  private participantStreams: Map<string, MediaStream> = new Map();
  private isInitialized: boolean = false;
  private isInitializing: boolean = false;
  private isConnecting: boolean = false;
  private initializePromise: Promise<void> | null = null;
  private producerToParticipant: Map<string, string> = new Map();
  private producerToDeviceId: Map<string, string> = new Map();
  
  /** Current role assignment (as streamIds), if any */
  private currentHostId: string | null = null;
  private currentCoHostId: string | null = null;

  private ee = new SimpleEventEmitter();

  private constructor() {
    this.serverUrl = import.meta.env.VITE_MEDIASOUP_WS_URL;
  }

  private static readonly HOST_PRODUCER_IDS_KEY = 'mediasoup_host_producer_ids';
  private static readonly COHOST_PRODUCER_IDS_KEY = 'mediasoup_cohost_producer_ids';

  /**
   * Get singleton instance of MediaSoup service
   */
  public static getInstance(): MediasoupService {
    if (!MediasoupService.instance) {
      MediasoupService.instance = new MediasoupService();
    }
    return MediasoupService.instance;
  }

  // ----------------- Event handlers (matching MediaMTX pattern) -----------------

  on(
    event:
      | 'streamUpdate'
      | 'streamRemoved'
      | 'mute'
      | 'hand'
      | 'connected'
      | 'disconnected'
      | 'rolesChanged'
      | 'handRequest',
    cb: (p: any) => void
  ) { this.ee.on(event, cb); }

  off(event: string, cb: (p: any) => void) { this.ee.off(event, cb); }

  /** Snapshot for screens that mount later */
  getStreamSummaries(): Array<{ id: string; mediaStream?: MediaStream; meta?: any }> {
    const summaries: Array<{ id: string; mediaStream?: MediaStream; meta?: any }> = [];
    this.participantStreams.forEach((mediaStream, streamId) => {
      summaries.push({
        id: streamId,
        mediaStream,
        meta: {
          hasVideo: mediaStream.getVideoTracks().length > 0,
          hasAudio: mediaStream.getAudioTracks().length > 0,
        }
      });
    });
    return summaries;
  }

  /** Snapshot of current role ids (may be nulls) */
  getCurrentRoles() {
    return { hostId: this.currentHostId, coHostId: this.currentCoHostId };
  }

  /**
   * Initialize MediaSoup service with proper connection handling
   */
  async initialize(): Promise<void> {
    if (this.isInitializing) {
      console.log('MediaSoup service already initializing, waiting...');
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return;
    }

    if (this.isInitialized && this.socket?.connected) {
      console.log('MediaSoup WebSocket already connected');
      return;
    }

    console.log('Starting MediaSoup service initialization...');
    this.isInitializing = true;

    try {
      await this.connect();
      await this.startMediasoupFlow();
      this.isInitialized = true;
      console.log('MediaSoup WebSocket service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize MediaSoup service:', error);
      this.isInitialized = false;
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Establish WebSocket connection to MediaSoup server
   */
  private async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Clean up existing socket if any
      if (this.socket) {
        console.log('Cleaning up existing MediaSoup socket connection');
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      console.log('Connecting to MediaSoup WebSocket at:', this.serverUrl);

      const socketUrl = `${this.serverUrl}`;
      this.socket = io(socketUrl, {
        transports: ["websocket"],
        upgrade: false,
        forceNew: true,
        autoConnect: true,
        reconnection: false, // manual reconnection
        timeout: 10000,
        pingTimeout: 60000,
        pingInterval: 25000,
        rememberUpgrade: false,
        upgradeTimeout: 30000
      });

      const connectionTimeout = setTimeout(() => {
        if (!this.socket?.connected) {
          console.error('MediaSoup connection timeout');
          reject(new Error('Connection timeout'));
        }
      }, 10000);

      this.socket.on("connect", () => {
        clearTimeout(connectionTimeout);
        console.log('MediaSoup WebSocket connected successfully');
        this.setupProducerEventListeners();
        this.setupNotificationListeners();
        this.ee.emit("connected", {});
        resolve();
      });

      this.socket.on("connect_error", (error) => {
        clearTimeout(connectionTimeout);
        console.error("MediaSoup WebSocket connection error:", error);
        reject(error);
      });

      this.socket.on("disconnect", (reason: string, details?: any) => {
        console.log('MediaSoup WebSocket disconnected:', reason, details);
        this.isInitialized = false;
        this.ee.emit("disconnected", { reason, details });
      });

      // Listen for transport creation notifications from server
      this.socket.on("transportCreated", async (data: any) => {
        if (!data.isProducer && this.device && this.device.loaded) {
          try {
            await this.createRecvTransportFromData(data);
          } catch (error) {
            console.error("MediaSoup: Failed to create transport from server notification -", error);
          }
        }
      });

      // Listen for reconnection requests
      this.socket.on("reconnectTransport", async (data: any) => {
        await this.handleTransportReconnection(data);
      });
    });
  }

  /**
   * Setup notification event listeners for operator notifications
   */
  private setupNotificationListeners(): void {
    if (!this.socket) return;

    // Listen for device hand raise notifications (REQUEST style)
    this.socket.on("deviceRaisedHand", (data: any) => {
      this.handleHandRaiseRequest({
        requestId: `${data.device_id}:${data.timestamp || Date.now()}`,
        streamId: String(data.device_id),
        raised: true,
        timestamp: data.timestamp
      });
    });

    // Listen for device hand lower notifications (REQUEST style)
    this.socket.on("deviceLoweredHand", (data: any) => {
      this.handleHandRaiseRequest({
        requestId: `${data.device_id}:${data.timestamp || Date.now()}`,
        streamId: String(data.device_id),
        raised: false,
        timestamp: data.timestamp
      });
    });

    this.socket.on("deviceSelfMute", (data: any) => {
      console.log('MediaSoup: Device self mute event:', data);
      this.ee.emit('deviceSelfMute', {
        streamId: data.streamId,
        selfMuted: data.self_muted
      });
    });

    this.socket.on("device_generic_request_ack", (data: any) => {
      this.handleGenericRequestAck(data);
    });

    // Listen for device left broadcast notifications
    this.socket.on("deviceLeftBroadcast", (data: any) => {
      this.ee.emit("streamRemoved", {
        id: data.device_id,
        online: false
      });
    });

    // Future request handlers (matching MediaMTX pattern)
    this.socket.on('mute_request', (d: any) => this.handleGenericRequest('mute', d));
    this.socket.on('unmute_request', (d: any) => this.handleGenericRequest('unmute', d));
    this.socket.on('host_request', (d: any) => this.handleGenericRequest('host', d));
    this.socket.on('co_host_request', (d: any) => this.handleGenericRequest('co-host', d));
  }



  private async handleGenericRequestAck(data: any): Promise<void> {
    console.log('MediaSoup: Received generic request ACK:', data);
    
    const { type, acknowledged, streamId } = data;
    
    if (!acknowledged) {
      console.warn('MediaSoup: Request was not acknowledged by server:', data);
      return;
    }

    // If it's a hand-related acknowledgment, refresh the raised hands list
    if (type === 'hand-lower' || type === 'hand-raise') {
      console.log('MediaSoup: Hand-related ACK received, refreshing raised hands list');
      
      try {
        // Request updated list from server
        const response = await this.requestRaiseHandList();
        
        // Emit event to trigger UI update
        this.ee.emit('handRequest', {
          type: 'list_updated',
          raisedList: response.raisedList
        });
        
        // Also emit individual hand events for UI consistency
        if (type === 'hand-lower' && streamId) {
          this.ee.emit('hand', { id: streamId, isHandRaised: false });
        }
        
      } catch (error) {
        console.error('MediaSoup: Failed to refresh raised hands list after ACK:', error);
      }
    }
    
    // Emit the acknowledgment event for other components that might need it
    this.ee.emit('requestAck', data);
  }

  // ---------- Request handlers (ACK "received") ----------

  /** Specialized handler for hand-raise (and lower via raised=false) requests */
  private handleHandRaiseRequest(data: any): void {
    const isRaise = data?.raised !== false; // default to raise when missing
    const req: HandRaiseRequest = {
      requestId: String(data?.requestId ?? ''),
      streamId: String(data?.streamId ?? ''),
      userId: data?.userId ? String(data.userId) : undefined,
      raised: isRaise,
      timestamp: data?.timestamp ?? Date.now(),
    };

    if (!req.requestId || !req.streamId) {
      console.warn('hand_raise_request missing requestId/streamId', data);
      return;
    }

    const ackType: GenericRequestAckDto['type'] = isRaise ? 'hand-raise' : 'hand-lower';

    // ACK RECEIVED with correct type
    this.sendRequestAck({
      requestId: req.requestId,
      streamId: req.streamId,
      type: ackType,
      received: true,
      processed: false,
      message: isRaise ? 'Hand-raise request received' : 'Hand-lower request received',
      payload: { userId: req.userId, timestamp: req.timestamp }
    });

    // Bubble to UI
    this.ee.emit('handRequest', req);
    this.ee.emit('hand', { id: req.streamId, isHandRaised: req.raised });
  }




  /** Generic request receiver (for future request events) */
  private handleGenericRequest(type: GenericRequestAckDto['type'], data: any): void {
    const requestId = String(data?.requestId ?? '');
    const streamId = String(data?.streamId ?? '');

    if (!requestId || !streamId) {
      console.warn(`${type}_request missing requestId/streamId`, data);
      return;
    }

    this.sendRequestAck({
      requestId,
      streamId,
      type,
      received: true,
      processed: false,
      message: `${type} request received`,
      payload: data
    });

    // For now we don't surface a UI event for generic requests.
    // Add if you later need panels for mute/host requests, etc.
  }

  // ----------------- Admin API (WS emits) -----------------

  private sendMessage(eventName: string, data: any): void {
    if (!this.socket?.connected) {
      console.error('MediaSoup WS not connected, cannot send:', eventName);
      return;
    }
    console.log('Sending MediaSoup message:', eventName, data);
    this.socket.emit(eventName, data);
  }

  private getAckEventName(type: GenericRequestAckDto['type']): string {
    switch (type) {
      case 'hand-raise': return 'operator_generic_request_ack';
      case 'hand-lower': return 'operator_generic_request_ack';
      case 'mute': return 'operator_generic_request_ack';
      case 'unmute': return 'operator_generic_request_ack';
      case 'host': return 'operator_generic_request_ack';
      case 'co-host': return 'operator_generic_request_ack';
      default: return 'operator_generic_request_ack';
    }
  }

  private sendRequestAck(dto: GenericRequestAckDto) {
    if (!this.socket?.connected) {
      console.error('MediaSoup WS not connected, cannot send request ACK:', dto);
      return;
    }
    const eventName = this.getAckEventName(dto.type);
    console.log(`Sending ACK on "${eventName}"`, dto);
    this.socket.emit(eventName, dto);
  }

  /** Convenience: mark processed result for a previous request */
  private ackProcessed(
    requestId: string,
    streamId: string,
    type: GenericRequestAckDto['type'],
    processed: boolean,
    message?: string,
    payload?: any
  ) {
    this.sendRequestAck({
      requestId,
      streamId,
      type,
      received: true,
      processed,
      message,
      payload
    });
  }

  /** Set stream as Host (matching MediaMTX signature) */
  setHost(streamId: string, opts?: AckOptions) {
    // Store producer IDs in localStorage for broadcast API
    const producerIds = this.getProducerIdsForDevice(streamId);
    localStorage.setItem(MediasoupService.HOST_PRODUCER_IDS_KEY, JSON.stringify(producerIds));
    
    // Keep existing WebSocket message unchanged
    this.sendMessage('setHost', {
      deviceId: streamId,
      timestamp: new Date().toISOString()
    });
    
    this.currentHostId = streamId;
    if (this.currentCoHostId === streamId) this.currentCoHostId = null;
    this.ee.emit('rolesChanged', this.getCurrentRoles());

    // If this action is in response to a request, ACK processed
    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'host',
        opts.processed ?? true,
        opts.message ?? 'Host set successfully',
        opts.payload
      );
    }
  }

  /** Set stream as Co-Host (matching MediaMTX signature) */
  setCoHost(streamId: string, opts?: AckOptions) {
    // Store producer IDs in localStorage for broadcast API
    const producerIds = this.getProducerIdsForDevice(streamId);
    localStorage.setItem(MediasoupService.COHOST_PRODUCER_IDS_KEY, JSON.stringify(producerIds));
    
    // Keep existing WebSocket message unchanged
    this.sendMessage('setCoHost', {
      deviceId: streamId,
      timestamp: new Date().toISOString()
    });

    this.currentCoHostId = streamId;
    if (this.currentHostId === streamId) this.currentHostId = null;
    this.ee.emit('rolesChanged', this.getCurrentRoles());

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'co-host',
        opts.processed ?? true,
        opts.message ?? 'Co-host set successfully',
        opts.payload
      );
    }
  }

  // Add helper methods to retrieve stored producer IDs
  getHostProducerIds(): string[] {
    try {
      const stored = localStorage.getItem(MediasoupService.HOST_PRODUCER_IDS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  getCoHostProducerIds(): string[] {
    try {
      const stored = localStorage.getItem(MediasoupService.COHOST_PRODUCER_IDS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /** Remove Host role (matching MediaMTX signature) */
  removeHost(streamId?: string) {
    const idToRemove = streamId ?? this.currentHostId ?? undefined;

    if (idToRemove) {
      // Clear stored producer IDs
      localStorage.removeItem(MediasoupService.HOST_PRODUCER_IDS_KEY);
      
      this.sendMessage('removeHost', {
        deviceId: idToRemove,
        timestamp: new Date().toISOString(),
      });
      
      if (this.currentHostId === idToRemove) {
        this.currentHostId = null;
        this.ee.emit('rolesChanged', this.getCurrentRoles());
      }
    }
  }

  /** Remove Co-Host role (matching MediaMTX signature) */
  removeCoHost(streamId?: string) {
    const idToRemove = streamId ?? this.currentCoHostId ?? undefined;

    if (idToRemove) {
      // Clear stored producer IDs
      localStorage.removeItem(MediasoupService.COHOST_PRODUCER_IDS_KEY);
      
      this.sendMessage('removeCoHost', {
        deviceId: idToRemove,
        timestamp: new Date().toISOString(),
      });

      if (this.currentCoHostId === idToRemove) {
        this.currentCoHostId = null;
        this.ee.emit('rolesChanged', this.getCurrentRoles());
      }
    }
  }

  /** Swap roles locally (matching MediaMTX signature) */
  swapRoles(hostId?: string | null, coHostId?: string | null) {
    const current = this.getCurrentRoles();
    const currentHost = hostId ?? current.hostId;
    const currentCoHost = coHostId ?? current.coHostId;
    
    if (!currentHost || !currentCoHost) {
      console.warn('MediaSoup: Cannot swap roles - both host and co-host must be set');
      return;
    }

    console.log('MediaSoup: Swapping roles:', { 
      oldHost: currentHost, 
      oldCoHost: currentCoHost,
      newHost: currentCoHost,
      newCoHost: currentHost
    });

    // ✅ FIX: Send WebSocket messages directly without calling setHost/setCoHost
    // which would trigger another swap
    this.sendMessage('setHost', {
      deviceId: currentCoHost, // Co-host becomes new host
      timestamp: new Date().toISOString()
    });

    this.sendMessage('setCoHost', {
      deviceId: currentHost, // Host becomes new co-host
      timestamp: new Date().toISOString()
    });

    // Update local state directly
    this.currentHostId = currentCoHost;
    this.currentCoHostId = currentHost;

    // Swap producer IDs in localStorage as well
    const hostProducerIds = this.getHostProducerIds();
    const coHostProducerIds = this.getCoHostProducerIds();
    
    // Store swapped producer IDs
    localStorage.setItem(MediasoupService.HOST_PRODUCER_IDS_KEY, JSON.stringify(coHostProducerIds));
    localStorage.setItem(MediasoupService.COHOST_PRODUCER_IDS_KEY, JSON.stringify(hostProducerIds));

    // Emit roles changed event
    this.ee.emit('rolesChanged', this.getCurrentRoles());
  }

  /** MuteActionDto: { streamId: string; muted: boolean } (matching MediaMTX signature) */
  setMicMuted(streamId: string, muted: boolean, opts?: AckOptions) {
    this.sendMessage('adminToggleMute', {
      device_id: streamId,
      muted,
      timestamp: new Date().toISOString(),
    });

    // Emit local mute event
    this.ee.emit('mute', { id: streamId, muteState: { effectivelyMuted: muted } });

    // ACK if this is settling a server-issued request
    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        muted ? 'mute' : 'unmute',
        opts.processed ?? true,
        opts.message ?? (muted ? 'Muted successfully' : 'Unmuted successfully'),
        opts.payload
      );
    }
  }

  /** HandRaiseDto: { streamId: string; raised: boolean } (matching MediaMTX signature) */
  lowerHand(streamId: string, opts?: AckOptions) {
    this.sendMessage('lowerHand', {
      streamId,
      raised: false,
      timestamp: new Date().toISOString(),
    });

    // Emit local hand event
    this.ee.emit('hand', { id: streamId, isHandRaised: false });

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'hand-lower',
        opts.processed ?? true,
        opts.message ?? 'Hand lowered',
        opts.payload
      );
    }
  }


  public rejectHand(streamId: string, opts?: AckOptions) {
    this.sendMessage('rejectHand', {
      device_id: streamId,
      timestamp: new Date().toISOString(),
    });

    // Emit local hand event to update UI
    this.ee.emit('hand', { id: streamId, isHandRaised: false });

    if (opts?.requestId) {
      this.ackProcessed(
        opts.requestId,
        streamId,
        'hand-lower', // or create a new 'hand-reject' type if needed
        opts.processed ?? true,
        opts.message ?? 'Hand rejected by operator',
        opts.payload
      );
    }
  }

  // --- Helper to collect both (audio+video) producerIds for a device ---
  public getProducerIdsForDevice(deviceId: string): string[] {
    const ids: string[] = [];
    const target = String(deviceId);
    console.log('Looking for producers for deviceId:', target);
    console.log('Available mappings:', Array.from(this.producerToDeviceId.entries()));
    this.producerToDeviceId.forEach((devId, producerId) => {
      if (String(devId) === target) {
        ids.push(producerId);
        console.log('Found producer:', producerId, 'for device:', devId);
      }
    });
    console.log('Final producer IDs:', ids);
    return ids;
  }

  // ----------------- Existing MediaSoup implementation continues... -----------------

  /**
   * Setup producer event listeners for auto-consuming new streams
   */
  private setupProducerEventListeners(): void {
    if (!this.socket) return;

    // Listen for new producer notifications
    this.socket.on("newProducer", async (data: any) => {
      try {
        const { producerId, device_id } = data || {};
        if (producerId && device_id) {
          this.producerToDeviceId.set(producerId, String(device_id)); // 🔑 map
        }
        await this.handleNewProducerNotification(data);
      } catch (error) {
        console.error("MediaSoup: Failed to handle new producer notification -", error);
      }
    });

    // Listen for producer left notifications
    this.socket.on("producerLeft", async (data: any) => {
      try {
        await this.handleProducerLeftNotification(data);
      } catch (error) {
        console.error("MediaSoup: Failed to handle producer left notification -", error);
      }
    });
  }

  /**
   * Handle new producer notification and auto-consume
   */
  private async handleNewProducerNotification(data: any): Promise<void> {
    try {
      const { producerId } = data;

      if (!this.consumerTransport || !this.roomId) {
        return;
      }

      // Create consumer for the new producer
      this.socket!.emit(
        "createConsumer",
        {
          roomId: this.roomId,
          transportId: this.consumerTransport.id,
          rtpCapabilities: this.device?.rtpCapabilities,
        },
        async (response: any) => {
          try {
            if (response && response.consumers && Array.isArray(response.consumers)) {
              // Find the consumer for this specific producer
              const newConsumer = response.consumers.find(
                (consumer: any) => consumer.producerId === producerId
              );

              if (newConsumer) {
                await this.consumeIndividually(newConsumer);
              }
            }
          } catch (error) {
            console.error("MediaSoup: Error consuming new producer -", error);
          }
        }
      );
    } catch (error) {
      console.error("MediaSoup: Failed to handle new producer -", error);
    }
  }

  /**
   * Handle producer left notification and stop consuming
   */
  private async handleProducerLeftNotification(data: any): Promise<void> {
    try {
      const { device_id } = data;

      // Find consumers associated with this device and close them
      const consumersToClose: any[] = [];

      this.activeConsumers.forEach((consumer, producerId) => {
        const participantId = this.producerToParticipant.get(producerId);
        if (participantId && participantId.includes(device_id)) {
          consumersToClose.push({ consumer, producerId });
        }
      });

      for (const { consumer, producerId } of consumersToClose) {
        try {
          consumer.close();
          this.consumers.delete(consumer.id);
          this.activeConsumers.delete(producerId);

          // Remove from participant streams
          const participantId = this.producerToParticipant.get(producerId);
          if (participantId) {
            this.removeTrackFromParticipantStream(
              producerId,
              participantId,
              consumer.track?.kind
            );
          }
        } catch (error) {
          console.error("MediaSoup: Error closing consumer for left producer -", error);
        }
      }
    } catch (error) {
      console.error("MediaSoup: Failed to handle producer left -", error);
    }
  }

  /**
   * Create receive transport from server provided data
   */
  private async createRecvTransportFromData(transportData: any): Promise<void> {
    try {
      if (!this.device) {
        throw new Error("Device not initialized");
      }

      // Close existing transport if any
      if (this.consumerTransport) {
        this.consumerTransport.close();
      }

      this.consumerTransport = this.device.createRecvTransport({
        id: transportData.transportId,
        iceParameters: transportData.iceParameters,
        iceCandidates: transportData.iceCandidates,
        dtlsParameters: transportData.dtlsParameters,
      });

      this.setupTransportEventListeners();

      // Manually trigger connection
      await this.manuallyTriggerConnection();
    } catch (error) {
      console.error("MediaSoup: Failed to create receive transport from data -", error);
      throw error;
    }
  }

  /**
   * Handle transport reconnection requests from server
   */
  private async handleTransportReconnection(data: any): Promise<void> {
    try {
      if (!this.consumerTransport || this.consumerTransport.id !== data.transportId) {
        console.warn("MediaSoup: Transport mismatch for reconnection, skipping");
        return;
      }

      // Check transport state
      if (this.consumerTransport.connectionState === "connected") {
        return;
      }

      // Try to manually trigger connection again
      await this.manuallyTriggerConnection();

      // Notify server that we're ready
      if (this.socket) {
        this.socket.emit("transportConnected", {
          transportId: data.transportId,
          isProducer: false,
          roomId: data.roomId,
        });
      }
    } catch (error) {
      console.error("MediaSoup: Failed to reconnect transport -", error);
    }
  }

  /**
   * Remove track from participant stream when producer is removed
   */
  private removeTrackFromParticipantStream(
    producerId: string,
    participantId: string,
    kind: string
  ): void {
    try {
      const stream = this.participantStreams.get(participantId);
      if (!stream) {
        console.warn(`MediaSoup: No stream found for participant: ${participantId}`);
        return;
      }

      // Remove tracks of the specific kind
      const tracksToRemove = stream
        .getTracks()
        .filter((track) => track.kind === kind);

      tracksToRemove.forEach((track) => {
        stream.removeTrack(track);
        track.stop(); // Stop the track to free resources
      });

      // Check if stream still has any tracks
      const remainingTracks = stream.getTracks();
      if (remainingTracks.length === 0) {
        // No tracks left, remove the entire stream
        this.participantStreams.delete(participantId);

        this.ee.emit("streamRemoved", {
          id: participantId,
          online: false,
          reason: "no_tracks_remaining",
        });
      } else {
        // Stream still has tracks, update the stream info
        const hasVideo = stream.getVideoTracks().length > 0;
        const hasAudio = stream.getAudioTracks().length > 0;

        this.ee.emit("streamUpdate", {
          id: participantId,
          mediaStream: stream,
          hasVideo,
          hasAudio,
          online: true,
          removedKind: kind,
        });
      }

      // Clean up producer to participant mapping
      this.producerToParticipant.delete(producerId);
    } catch (error) {
      console.error("MediaSoup: Failed to remove track from participant stream -", error);
    }
  }

  /**
   * Join MediaSoup room and setup device capabilities
   */
  private async joinRoom(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error("Socket not available"));
        return;
      }

      this.socket.emit("joinRoom", async (response: any) => {
        try {
          if (
            response &&
            response.success &&
            response.roomId &&
            response.transport
          ) {
            this.roomId = response.roomId;

            if (this.device && !this.device.loaded) {
              await this.device.load({
                routerRtpCapabilities: response.rtpCapabilities,
              });
            }

            await this.createRecvTransport(response.transport);

            // Notify server that we have our transport ready
            if (this.socket && this.consumerTransport) {
              this.socket.emit("transportConnected", {
                transportId: this.consumerTransport.id,
                isProducer: false,
                roomId: this.roomId,
              });
            }

            resolve();
          } else {
            reject(new Error("Failed to join room or invalid response"));
          }
        } catch (error) {
          console.error("MediaSoup: Error in joinRoom callback -", error);
          reject(error);
        }
      });
    });
  }

  /**
   * Start the MediaSoup flow - join room and create consumers
   */
  private async startMediasoupFlow(): Promise<void> {
    try {
      this.device = new Device();
      await this.joinRoom();
      await this.createConsumers();
    } catch (error) {
      console.error("MediaSoup: MediaSoup flow failed -", error);
      throw error;
    }
  }

  /**
   * Create receive transport for consuming media streams
   */
  private async createRecvTransport(transportData: any): Promise<void> {
    try {
      if (!this.device) {
        throw new Error("Device not initialized");
      }

      this.consumerTransport = this.device.createRecvTransport({
        id: transportData.transportId,
        iceParameters: transportData.iceParameters,
        iceCandidates: transportData.iceCandidates,
        dtlsParameters: transportData.dtlsParameters,
      });

      this.setupTransportEventListeners();
      this.consumerTransport.serverTransportData = transportData;
    } catch (error) {
      console.error("MediaSoup: Failed to create receive transport -", error);
      throw error;
    }
  }

  /**
   * Setup event listeners for transport state changes
   */
  private setupTransportEventListeners(): void {
    if (!this.consumerTransport) return;

    this.consumerTransport.on("connectionstatechange", (state: string) => {
      if (state === "failed") {
        console.error("MediaSoup: Transport connection failed");
        this.handleTransportFailure();
      } else if (state === "closed") {
        console.warn("MediaSoup: Consumer transport closed");
      }
    });
  }

  /**
   * Create consumers for available producers in the room
   */
  private async createConsumers(): Promise<void> {
    if (!this.socket || !this.roomId || !this.consumerTransport) {
      throw new Error("Missing requirements for consumer creation");
    }

    this.setupConnectHandler();

    return new Promise((resolve, reject) => {
      this.socket!.emit(
        "createConsumer",
        {
          roomId: this.roomId,
          transportId: this.consumerTransport.id,
          rtpCapabilities: this.device?.rtpCapabilities,
        },
        async (response: any) => {
          try {
            if (response && Array.isArray(response.consumers)) {
              const list = response.consumers as any[];

              // 🔑 PRE-PASS: cache producerId -> device_id from the snapshot
              for (const c of list) {
                const devId = c?.device_id ?? c?.deviceId;
                if (c?.producerId && devId) {
                  this.producerToDeviceId.set(c.producerId, String(devId));
                }
              }

              if (list.length === 0) {
                await this.manuallyTriggerConnection();
              } else {
                for (const consumerData of list) {
                  await this.consumeIndividually(consumerData);
                }
              }
            } else {
              await this.manuallyTriggerConnection();
            }
            resolve();
          } catch (error) {
            console.error("MediaSoup: Error creating consumers -", error);
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Manually trigger transport connection when no consumers are available
   */
  private async manuallyTriggerConnection(): Promise<void> {
    if (!this.consumerTransport || !this.socket) {
      console.warn("MediaSoup: Cannot manually trigger - missing transport or socket");
      return;
    }

    try {
      const dtlsParameters = this.consumerTransport.dtlsParameters;

      if (!dtlsParameters) {
        console.error("MediaSoup: No DTLS parameters available for manual connection");
        return;
      }

      return new Promise((resolve, reject) => {
        this.socket!.emit(
          "connectTransport",
          {
            roomId: this.roomId,
            transportId: this.consumerTransport.id,
            dtlsParameters: dtlsParameters,
          },
          (response: any) => {
            if (response && (response.connected || response.success)) {
              resolve();
            } else {
              console.error("MediaSoup: Manual transport connection failed -", response?.error || "Unknown error");
              reject(new Error(`Manual connection failed: ${response?.error || "Unknown error"}`));
            }
          }
        );
      });
    } catch (error) {
      console.error("MediaSoup: Error in manual connection trigger -", error);
    }
  }

  /**
   * Setup connect event handler for transport
   */
  private setupConnectHandler(): void {
    if (!this.consumerTransport) return;

    this.consumerTransport.removeAllListeners("connect");

    this.consumerTransport.on(
      "connect",
      ({ dtlsParameters }: any, callback: any, errback: any) => {
        if (!this.socket) {
          const error = new Error("Socket not available during connect");
          if (errback) errback(error);
          else callback(error);
          return;
        }

        this.socket.emit(
          "connectTransport",
          {
            roomId: this.roomId,
            transportId: this.consumerTransport.id,
            dtlsParameters: dtlsParameters,
          },
          (response: any) => {
            if (response && (response.connected || response.success)) {
              callback();
            } else {
              console.error("MediaSoup: Transport connection failed on server side -", response?.error || "Unknown error");
              const error = new Error(`Transport connection failed: ${response?.error || "Unknown error"}`);
              if (errback) {
                errback(error);
              } else {
                callback(error);
              }
            }
          }
        );
      }
    );
  }

  /**
   * Create individual consumer for a producer
   */
  private async consumeIndividually(consumerData: any): Promise<void> {
    try {
      if (!this.consumerTransport) {
        console.error("MediaSoup: No consumer transport available");
        return;
      }

      if (this.activeConsumers.has(consumerData.producerId)) {
        return;
      }

      const consumer = await this.consumerTransport.consume({
        id: consumerData.consumerId,
        producerId: consumerData.producerId,
        kind: consumerData.kind,
        rtpParameters: consumerData.rtpParameters,
        appData: consumerData.appData || {},
      });

      this.consumers.set(consumer.id, consumer);
      this.activeConsumers.set(consumerData.producerId, consumer);

      if (consumer.paused) {
        await consumer.resume();
      }

      const track = consumer.track;
      if (!track) {
        console.error("MediaSoup: No track available for consumer");
        return;
      }

      this.setupTrackEventListeners(track, consumerData);
      this.addTrackToParticipantStream(track, consumerData);

      if (consumerData.kind === "video") {
        this.monitorConsumerStats(consumer);
      }
    } catch (error) {
      console.error("MediaSoup: Failed to consume individually -", error);
    }
  }

  private setupTrackEventListeners(track: MediaStreamTrack, consumerData: any): void {
    track.addEventListener("ended", () => {
      this.handleTrackEnded(consumerData.producerId);
    });
  }

  /**
   * Get participant ID from consumer data with fallback strategies
   */
  private getParticipantIdFor(consumerData: any): string {
    // 1) Direct field from snapshot/newProducer payloads
    const directDev =
      consumerData?.device_id ??
      consumerData?.deviceId ??
      consumerData?.appData?.device_id ??
      consumerData?.appData?.deviceId;

    if (directDev) {
      // keep the cache warm so later events (e.g., track end/newProducer) resolve immediately
      const prodId = consumerData?.producerId;
      if (prodId) this.producerToDeviceId.set(String(prodId), String(directDev));
      return String(directDev);
    }

    // 2) Explicit participant ids in appData (if your server ever sets them)
    const fromApp =
      consumerData?.appData?.participantId || consumerData?.appData?.peerId;
    if (fromApp) return String(fromApp);

    // 3) Cached mapping from earlier newProducer/snapshot pre-pass
    const prodId = consumerData?.producerId;
    const hinted = prodId ? this.producerToDeviceId.get(String(prodId)) : undefined;
    if (hinted) return String(hinted);

    // 4) Last-resort: RTCP CNAME (shared across tracks but stable enough)
    const cname = consumerData?.rtpParameters?.rtcp?.cname;
    if (cname) return `cname-${String(cname)}`;

    // 5) Ultimate fallback: producer prefix (keeps audio+video separate)
    return `producer-${String(prodId || "").substring(0, 8)}`;
  }

  /**
   * Add track to participant's media stream
   */
  private addTrackToParticipantStream(track: MediaStreamTrack, consumerData: any): void {
    const participantId = this.getParticipantIdFor(consumerData);

    this.producerToParticipant.set(consumerData.producerId, participantId);

    let mediaStream = this.participantStreams.get(participantId);
    if (!mediaStream) {
      mediaStream = new MediaStream();
      this.participantStreams.set(participantId, mediaStream);
    }

    // Remove existing tracks of the same kind
    mediaStream
      .getTracks()
      .filter((t) => t.kind === track.kind)
      .forEach((t) => mediaStream!.removeTrack(t));

    mediaStream.addTrack(track);

    const hasVideo = mediaStream.getVideoTracks().length > 0;
    const hasAudio = mediaStream.getAudioTracks().length > 0;

    // Emit streamUpdate instead of streamAvailable to match MediaMTX
    this.ee.emit("streamUpdate", {
      id: participantId,
      mediaStream: mediaStream,
      kind: hasVideo && hasAudio ? "video+audio" : track.kind,
      hasVideo,
      hasAudio,
      meta: {
        participantName: consumerData?.appData?.deviceName || `Participant ${participantId}`,
        deviceName: consumerData?.appData?.deviceName || `Device ${participantId}`,
        producerId: consumerData.producerId,
      },
      online: true
    });
  }

  /**
   * Handle track ended event
   */
  private handleTrackEnded(producerId: string): void {
    const consumer = this.activeConsumers.get(producerId);
    if (consumer) {
      consumer.close();
      this.consumers.delete(consumer.id);
      this.activeConsumers.delete(producerId);
    }

    const participantId =
      this.producerToParticipant.get(producerId) ||
      `producer-${producerId.substring(0, 8)}`;

    const stream = this.participantStreams.get(participantId);
    if (stream) {
      const stillHasVideo = stream.getVideoTracks().length > 0;
      const stillHasAudio = stream.getAudioTracks().length > 0;

      if (!stillHasVideo && !stillHasAudio) {
        this.participantStreams.delete(participantId);
        this.ee.emit("streamRemoved", {
          id: participantId,
          online: false
        });
      } else {
        this.ee.emit("streamUpdate", {
          id: participantId,
          mediaStream: stream,
          hasVideo: stillHasVideo,
          hasAudio: stillHasAudio,
          meta: {
            participantName: `Participant ${participantId}`,
            deviceName: `Device ${participantId}`,
          },
          online: true
        });
      }
    }
    this.producerToDeviceId.delete(producerId);
    this.producerToParticipant.delete(producerId);
  }

  /**
   * Handle transport failure and attempt recovery
   */
  private async handleTransportFailure(): Promise<void> {
    try {
      if (this.consumerTransport) {
        this.consumerTransport.close();
        this.consumerTransport = null;
      }

      this.consumers.clear();
      this.activeConsumers.clear();
      this.participantStreams.clear();

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await this.joinRoom();
      await this.createConsumers();
    } catch (error) {
      console.error("MediaSoup: Failed to recover transport -", error);
      setTimeout(() => this.handleTransportFailure(), 5000);
    }
  }

  /**
   * Monitor consumer statistics for debugging purposes
   */
  private monitorConsumerStats(consumer: any): void {
    const statsInterval = setInterval(async () => {
      try {
        if (consumer.closed) {
          clearInterval(statsInterval);
          return;
        }

        const stats = await consumer.getStats();
        let bytesReceived = 0;

        stats.forEach((report: any) => {
          if (report.type === "inbound-rtp" && report.mediaType === "video") {
            bytesReceived = report.bytesReceived || 0;
          }
        });

        if (bytesReceived === 0) {
          console.warn("MediaSoup: No video data being received for consumer");
        }
      } catch (error) {
        console.error("MediaSoup: Failed to get consumer stats -", error);
        clearInterval(statsInterval);
      }
    }, 5000);
  }

  /**
   * Check if service is connected to server
   */
  public isConnected(): boolean {
    return !!this.socket?.connected;
  }

  /**
   * Disconnect from MediaSoup service and cleanup resources
   */
  disconnect(): void {
    console.log('Disconnecting MediaSoup WebSocket service');
    this.isInitialized = false;
    this.isInitializing = false;

    // Close all consumers
    this.consumers.forEach((consumer) => {
      try {
        consumer.close();
      } catch (error) {
        console.warn("MediaSoup: Error closing consumer -", error);
      }
    });

    // Close transports
    if (this.consumerTransport) {
      try {
        this.consumerTransport.close();
      } catch (error) {
        console.warn("MediaSoup: Error closing consumer transport -", error);
      }
      this.consumerTransport = null;
    }

    if (this.producerTransport) {
      try {
        this.producerTransport.close();
      } catch (error) {
        console.warn("MediaSoup: Error closing producer transport -", error);
      }
      this.producerTransport = null;
    }

    // Disconnect socket
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear all maps and reset state
    this.consumers.clear();
    this.activeConsumers.clear();
    this.participantStreams.clear();
    this.producerToParticipant.clear();
    this.producerToDeviceId.clear();
    this.device = null;
    this.roomId = null;
    this.isInitialized = false;
    this.isConnecting = false;
    this.currentHostId = null;
    this.currentCoHostId = null;
  }

  /**
   * Get available streams (legacy method for compatibility)
   * @deprecated Use getStreamSummaries() instead
   */
  public getAvailableStreams(): Map<string, MediaStream> {
    return new Map(this.participantStreams);
  }

  /**
   * Listen for stream availability (legacy method for compatibility)
   * @deprecated Use on('streamUpdate') instead
   */
  public onStreamAvailable(callback: (data: { streamId: string; mediaStream: MediaStream; hasVideo?: boolean; hasAudio?: boolean }) => void): void {
    this.on('streamUpdate', (data) => {
      if (data.online && data.mediaStream) {
        callback({
          streamId: data.id,
          mediaStream: data.mediaStream,
          hasVideo: data.hasVideo,
          hasAudio: data.hasAudio
        });
      }
    });
  }

  /**
   * Remove stream listener (legacy method for compatibility)
   * @deprecated Use off('streamUpdate') instead
   */
  public removeStreamListener(callback: Function): void {
    this.off('streamUpdate', callback);
  }

  /**
   * Promote device to host (legacy method for compatibility)
   * @deprecated Use setHost() instead
   */
  public async promoteDeviceToHost(deviceId: string): Promise<void> {
    this.setHost(deviceId);
  }

  /**
   * Promote device to co-host (legacy method for compatibility)
   * @deprecated Use setCoHost() instead
   */
  public async promoteDeviceToCoHost(deviceId: string): Promise<void> {
    this.setCoHost(deviceId);
  }

  /**
   * Request the current list of raised hands from the server
   */
  public requestRaiseHandList(): Promise<{ raisedList: string[] }> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error("MediaSoup socket not connected"));
        return;
      }

      console.log('MediaSoup: Requesting raised hands list');
      this.socket.emit(
        "listRaiseHandRequests",
        (response: { raisedList: string[] }) => {
          console.log('MediaSoup: Received raised hands list:', response);
          resolve(response);
        }
      );
    });
  }

  /**
   * Check if a specific device has raised hand
   */
  public isHandRaised(deviceId: string): Promise<boolean> {
    return this.requestRaiseHandList()
      .then(({ raisedList }) => raisedList.includes(String(deviceId)))
      .catch(() => false);
  }

  /**
   * Get all currently raised hands
   */
  public async getRaisedHands(): Promise<string[]> {
    try {
      const { raisedList } = await this.requestRaiseHandList();
      return raisedList;
    } catch (error) {
      console.error('MediaSoup: Failed to get raised hands:', error);
      return [];
    }
  }
}

export const mediasoupService = MediasoupService.getInstance();
export default MediasoupService;
